import { useState } from "react";
import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useBriefingCrewNames, useBriefingFlights } from "@/hooks/useBriefingFlights";
import { FlightsDialog } from "@/components/briefing/FlightsDialog";
import { flightIssues, legCountByCrewId } from "@produce/shared";

// The Flights block on the general briefing step: a toggle, a short
// summary of who has flights, and a button to the editing dialog.
//
// Everything heavier lives in the dialog on purpose — a production
// without flights should cost the producer one glance, not a page of
// empty fields.

export function FlightsSection({
  briefingId,
  enabled,
  onEnabledChange,
}: {
  briefingId: string | null | undefined;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, setEnabled } = useBriefingFlights(enabled ? briefingId : null);

  const { data: crew = [] } = useBriefingCrewNames(briefingId, enabled);

  /**
   * Write the toggle straight away instead of leaving it to the wizard's
   * briefing save.
   *
   * Producers switch Flights on, enter every flight in the dialog and
   * navigate away — and the toggle stayed false in the database. The
   * flights existed but the section was hidden everywhere: not on the
   * review page, not in the crew app. On failure the switch flips back,
   * so what is on screen always matches what is stored.
   */
  const handleToggle = async (next: boolean) => {
    onEnabledChange(next);
    if (!briefingId) return;
    try {
      await setEnabled.mutateAsync(next);
    } catch {
      onEnabledChange(!next);
      toast.error("Could not change the flights setting.");
    }
  };

  const counts = legCountByCrewId(data.passengers);
  const withFlights = crew.filter((member) => counts[member.id] > 0);
  const issues = flightIssues(data.legs, data.passengers);

  return (
    <Card className="bg-card p-6">
      <h3 className="mb-4 text-lg font-semibold">Flights</h3>

      <div className="mb-4 flex items-center gap-2">
        <Switch
          checked={enabled}
          onCheckedChange={(next) => void handleToggle(next)}
          id="flights-enabled"
        />
        <Label htmlFor="flights-enabled">Flights are booked for this production</Label>
      </div>

      {!enabled ? (
        <p className="text-sm text-muted-foreground">
          Turn this on when crew have to fly in. You then pick per crew member whose flight details
          to fill in — each of them only sees their own.
        </p>
      ) : !briefingId ? (
        <p className="text-sm text-muted-foreground">
          Save this production first, then add the flights.
        </p>
      ) : (
        <div className="space-y-4">
          {withFlights.length === 0 ? (
            <p className="text-sm text-muted-foreground">No flights added yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {withFlights.map((member) => (
                <li key={member.id} className="flex justify-between gap-4">
                  <span className="text-foreground">{member.name}</span>
                  <span className="text-muted-foreground">
                    {counts[member.id]} {counts[member.id] === 1 ? "flight" : "flights"}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {issues.length > 0 && (
            <p className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-500">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                {issues.length} {issues.length === 1 ? "detail is" : "details are"} still missing —
                open the flights to complete them.
              </span>
            </p>
          )}

          <Button type="button" variant="outline" onClick={() => setDialogOpen(true)}>
            Manage flights
          </Button>

          <FlightsDialog open={dialogOpen} onOpenChange={setDialogOpen} briefingId={briefingId} />
        </div>
      )}
    </Card>
  );
}
